import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import MaterialTable from "@material-table/core";
import { defaultComponents, MaterialTableBody, MaterialTableBodyRow } from '../../../controls/Material-Table-Components';

import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import {MaskedText, DatePicker, ComboSelect, MaskedTime} from "../../../controls";
import AircraftDispatchDeviationsValidator from "../validators/AircraftDispatchDeviationsValidator";
import {
  useAddAircraftDispatchDeviation,
  useAircraftDispatchDeviations, useDeleteAircraftDispatchDeviation,
  useUpdateAircraftDispatchDeviation
} from "../../../services/aircraft";
import AirportLookup from "../../Airports/components/AirportsLookup";
import moment from "moment";
import { useTheme } from '@material-ui/core';

const AccessControl = require ("../../../AccessControl");

const AircraftDefectsEditor = (_props) => {
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated, user} = useAuth0();
  const eventBus = SystemData.eventBus;
  if (eventBus) {
    eventBus.bind('Defects.AddDefect', ()=> {
      if (tableRef  && tableRef.current) {
        tableRef.current.dataManager.changeRowEditing();
        tableRef.current.setState({
          ...tableRef.current.dataManager.getRenderState(),
          showAddRow: !tableRef.current.state.showAddRow,
        });
      }
    });
  }

  const props = {
    ..._props
  }
  const {classes} = props;

  const [state] = useState({
    canEdit: AccessControl.userHasAccess(user, ["Super Admin", "Admin", "Manage AIRCRAFT"]),
    activeRow: null
  });

  const handleAddDispatchDeviation = useAddAircraftDispatchDeviation();
  const handleUpdateDispatchDeviation = useUpdateAircraftDispatchDeviation();
  const handleDeleteDispatchDeviation = useDeleteAircraftDispatchDeviation();

  const queryClient = useQueryClient();
  const extraKeys = [props.aircraftTailNumber];
  const dispatchDeviations = useAircraftDispatchDeviations({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: state.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const validatorColumn = ValidatorColumn(AircraftDispatchDeviationsValidator, true);

    const dateFormat = "DD/MM/YYYY";

    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(250,6,undefined,undefined,undefined,undefined,false),
        title: "Category",
        field: "deviationCategory",
        editable: state.canEdit ? "always" : "never",
        render: (rowData) => (
          <ComboSelect
            id="deviationCategory"
            group="DISPATCH DEVIATIONS"
            subGroup=""
            fullWidth
            value={rowData.deviationCategory}
            displayOnly={true}
            onChange={props.onChange}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="deviationCategory"
            group="DISPATCH DEVIATIONS"
            subGroup=""
            fullWidth
            value={props.value || ""}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(130,2,undefined,undefined,undefined,undefined,false),
        title: "Reported On",
        field: "reportedOn",
        type: "date",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            initialValue={moment.utc().format("YYYY-MM-DD")}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(60,2,undefined,undefined,undefined,undefined,false),
        title: "Time",
        field: "reportedTime",
        //type: "time",
        emptyValue: "00:00",
        defaultValue: "00:00",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedTime
            source="reportedTime"
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (props) => (
            <div style={{ width: 60 }}>
              {moment.utc(`9999-12-31 ${props.reportedTime||'00:00'}`, 'YYYY-MM-DD HH:mm').format("HH:mm")}
            </div>
          )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120, undefined, undefined, {paddingLeft: 2, paddingTop: 8}, {paddingLeft: 2})),
        title: "At Airport",
        field: "atAirport",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(200,2,undefined,undefined,undefined,undefined,false),
        title: "Details",
        field: "details",
        initialEditValue: "",
        editable: "always",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(200)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(130,2,undefined,undefined,undefined,undefined,false),
        title: "Rectified On",
        field: "completedOn",
        type: "date",
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        ),
      },
    ];
  };

  const bodyRef = createRef();

  return (isAuthenticated &&
    <div className={classes.DefectsEditor}>
      <MaterialTable
        tableRef={tableRef}
        data={dispatchDeviations.data}
        style={{
          backgroundColor: "#fdfdfd",
          boxShadow: "none",
          border: "2px solid #aaaaaa",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          search: false,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "8px 2px",
          },
          headerRowStyle: {
            display: "table",
            width: "100%",
          },
          toolbarButtonAlignment: "right",
          detailPanelType: "single",
          showTitle: false,
          toolbar: false,
          padding: 'dense',
          paging: false,
          filtering: false,
          draggable: false,
        }}
        components={{
          ...defaultComponents,
          Body: (props) => (
            <MaterialTableBody
              {...props}
              ref={bodyRef}
              rowHeight={46}
              overscanRowCount={10}
              addVirtualScrolling={true}
              bodyHeight={250}
              headerHeight={46}
            />
          ),
          Row: (props) => (
            <MaterialTableBodyRow {...props} />
          ),
        }}
        title="Deferred Defects"
        columns={getColumns({...props, canEdit: state.canEdit})}
        actions={[
        ]}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              handleAddDispatchDeviation.mutate({record: {...newData, aircraftUuid: props.aircraftUuid, aircraftTailNumber: props.aircraftTailNumber}, resolve, reject, extraKeys});
            })
            : undefined,
          onRowUpdate: state.canEdit
            ? (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateDispatchDeviation.mutate({record: {...newData}, oldRecord: oldData, resolve, reject, extraKeys});
              })
            : undefined,
          onRowDelete: state.canEdit
            ? (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteDispatchDeviation.mutate({oldRecord: {...oldData}, resolve, reject, extraKeys});
              })
            : undefined,
        }}
      />
    </div>
  );
}

export default AircraftDefectsEditor;
